import React from 'react';
import { Link } from 'react-router-dom';
import { ClipboardList, FileSearch, Sparkles, CalendarCheck2, Stethoscope, ArrowRight, HeartHandshake, Languages, ClipboardCheck } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { buildLocalizedPath } from './LocaleRouter';

const STEPS = [
  { icon: Stethoscope, key: 'step1' },
  { icon: ClipboardList, key: 'step2' },
  { icon: FileSearch, key: 'step3' },
  { icon: Sparkles, key: 'step4' },
  { icon: CalendarCheck2, key: 'step5' },
];

const HowVietClinicsWorks = () => {
  const { t, language } = useLanguage();
  return (
  <section className="py-20 lg:py-24" style={{ background: '#F5FAF8' }} data-testid="how-vietclinics-works">
    <div className="container mx-auto px-4 max-w-6xl">
      <div className="mb-12 lg:mb-16 max-w-3xl">
        <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
          {t('howItWorks.eyebrow')}
        </p>
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight leading-[1.1]">
          {t('howItWorks.title')}
        </h2>
        <p className="text-slate-500 mt-4 text-base lg:text-lg leading-relaxed">
          {t('howItWorks.subtitle')}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 lg:gap-4">
        {STEPS.map(({ icon: Icon, key }, idx) => (
          <div key={key} className="relative flex flex-col items-start" data-testid={`how-step-${idx + 1}`}>
            <div className="relative mb-5">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #DCEFEC, #C3E5E2)' }}
              >
                <Icon className="h-7 w-7 text-[#1A8F8A]" />
              </div>
              <span
                className="absolute -top-1 -left-1 w-6 h-6 rounded-full bg-white text-[10px] font-extrabold text-[#1FA7A1] flex items-center justify-center"
                style={{ boxShadow: '0 2px 6px rgba(15,42,85,0.10)' }}
              >
                {idx + 1}
              </span>
            </div>
            <h3 className="font-extrabold text-slate-900 text-base lg:text-[17px] leading-snug mb-2">
              {t(`howItWorks.${key}Title`)}
            </h3>
            <p className="text-slate-500 text-sm leading-relaxed">{t(`howItWorks.${key}Desc`)}</p>
            {idx < STEPS.length - 1 && (
              <ArrowRight aria-hidden className="hidden lg:block absolute top-5 -right-3 h-4 w-4 text-[#1FA7A1]/30" />
            )}
          </div>
        ))}
      </div>

      {/* General Patient Support cross-link — sits below the 5 steps so it
          is positioned as broad "patient journey" support, not urgent-care. */}
      <div
        className="mt-12 lg:mt-14 bg-white rounded-3xl border border-slate-100 p-6 lg:p-7 flex flex-col sm:flex-row items-start sm:items-center gap-5"
        style={{ boxShadow: '0 4px 20px rgba(15,42,85,0.05)' }}
        data-testid="home-patient-support-block"
      >
        <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#DCEFEC' }}>
          <HeartHandshake className="h-5 w-5 text-[#1A8F8A]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-1">
            Patient support
          </p>
          <p className="font-extrabold text-slate-900 text-base lg:text-lg leading-snug">
            Need help choosing a doctor, comparing clinics, or arranging a translator?
          </p>
          <p className="text-sm text-slate-600 leading-relaxed mt-1">
            VietClinics is an independent healthcare coordination platform — we help you
            understand your options and support you before, during and after treatment.
          </p>
        </div>
        <Link
          to={buildLocalizedPath('/patient-support-vietnam', language)}
          className="inline-flex items-center gap-2 bg-[#1FA7A1] hover:bg-[#138F8A] text-white font-bold text-sm rounded-full px-5 py-3 transition-colors flex-shrink-0"
          data-testid="home-patient-support-cta"
        >
          Explore Patient Support
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      {/* Doctor Recommendation cross-link — placed alongside Patient Support
          so both general-support entry points are visible on the homepage. */}
      <div
        className="mt-4 bg-white rounded-3xl border border-slate-100 p-6 lg:p-7 flex flex-col sm:flex-row items-start sm:items-center gap-5"
        style={{ boxShadow: '0 4px 20px rgba(15,42,85,0.05)' }}
        data-testid="home-doctor-recommendation-block"
      >
        <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#DCEFEC' }}>
          <Stethoscope className="h-5 w-5 text-[#1A8F8A]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-1">
            Doctor recommendation
          </p>
          <p className="font-extrabold text-slate-900 text-base lg:text-lg leading-snug">
            Looking for a suitable doctor, dentist, surgeon or specialist?
          </p>
          <p className="text-sm text-slate-600 leading-relaxed mt-1">
            We help request provider options for your needs and location.
            Credentials, language support and availability should be confirmed with the provider.
          </p>
        </div>
        <Link
          to={buildLocalizedPath('/doctor-recommendation-vietnam', language)}
          className="inline-flex items-center gap-2 bg-[#1FA7A1] hover:bg-[#138F8A] text-white font-bold text-sm rounded-full px-5 py-3 transition-colors flex-shrink-0"
          data-testid="home-doctor-recommendation-cta"
        >
          Doctor Recommendations
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      {/* Medical Translator cross-link — third general-support block. */}
      <div
        className="mt-4 bg-white rounded-3xl border border-slate-100 p-6 lg:p-7 flex flex-col sm:flex-row items-start sm:items-center gap-5"
        style={{ boxShadow: '0 4px 20px rgba(15,42,85,0.05)' }}
        data-testid="home-medical-translator-block"
      >
        <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#DCEFEC' }}>
          <ArrowRight className="h-5 w-5 text-[#1A8F8A] rotate-0" aria-hidden />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-1">
            Medical translator &amp; communication
          </p>
          <p className="font-extrabold text-slate-900 text-base lg:text-lg leading-snug">
            Need a translator or help understanding a treatment plan?
          </p>
          <p className="text-sm text-slate-600 leading-relaxed mt-1">
            We coordinate translator and English-speaking communication support where
            available — for appointments, hospital paperwork, aftercare instructions,
            and online consultations.
          </p>
        </div>
        <Link
          to={buildLocalizedPath('/medical-translator-vietnam', language)}
          className="inline-flex items-center gap-2 bg-[#1FA7A1] hover:bg-[#138F8A] text-white font-bold text-sm rounded-full px-5 py-3 transition-colors flex-shrink-0"
          data-testid="home-medical-translator-cta"
        >
          Translator Support
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      {/* How to Choose a Doctor cross-link — 4th general-support block. */}
      <div
        className="mt-4 bg-white rounded-3xl border border-slate-100 p-6 lg:p-7 flex flex-col sm:flex-row items-start sm:items-center gap-5"
        style={{ boxShadow: '0 4px 20px rgba(15,42,85,0.05)' }}
        data-testid="home-how-to-choose-doctor-block"
      >
        <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#DCEFEC' }}>
          <ClipboardCheck className="h-5 w-5 text-[#1A8F8A]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-1">
            How to choose
          </p>
          <p className="font-extrabold text-slate-900 text-base lg:text-lg leading-snug">
            Want a practical guide for choosing the right doctor in Vietnam?
          </p>
          <p className="text-sm text-slate-600 leading-relaxed mt-1">
            Comparison criteria, red flags, the 10 questions to ask, and when to
            seek a second opinion — independent patient guide.
          </p>
        </div>
        <Link
          to={buildLocalizedPath('/how-to-choose-doctor-vietnam', language)}
          className="inline-flex items-center gap-2 bg-[#1FA7A1] hover:bg-[#138F8A] text-white font-bold text-sm rounded-full px-5 py-3 transition-colors flex-shrink-0"
          data-testid="home-how-to-choose-doctor-cta"
        >
          Read the Guide
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  </section>
  );
};

export default HowVietClinicsWorks;
